import { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import Button from '../../../../../../ui/button/button';
import Icon from '../../../../../../common/icon/icon';
import { BUTTON_VARIANTS } from '../../../../../../constants/button-variants';
import Routes from '../../../../../../constants/routes';
import useChangeFavorite from '../../../../../../hooks/use-change-favorite';
import translation, { world } from '../../../../../../languages';
import { AVAILABLE_WORLDS } from '../../../../../../languages/constants';
import foldAnimation from '../../../../../../utils/animations/fold';
import schoolAccount from '../../../../../../utils/school-account';
import { useFavoritesPageContext } from '../../../../contexts/favorites-page-context';
import SchoolWishlistRemoveButton from '../../../school-wishlist-remove-button';
import { useCardContext } from '../../contexts/favorites-material-card-context';
import './material-remove-button.scss';

const REMOVE_ANIMATION_DURATION = 600;

type MaterialRemoveButtonProps = TranslationProps & {
    item: Material;
    index?: number;
    type: 'icon' | 'removeOnly';
};

const MaterialRemoveButton = (props: MaterialRemoveButtonProps): ReactElement => {
    const { t, item, index, type } = props;

    const { changeFavoriteItem } = useChangeFavorite();
    const user = useSelector((state: RootState) => state.main.user);
    const { cardRef, onRemove } = useCardContext();
    const { areButtonsDisabled, disableButtons, enableButtons } = useFavoritesPageContext();

    const isSchoolMaster = schoolAccount.isAdmin(user) && world === AVAILABLE_WORLDS.DE;
    const isSchoolWishlistPage = window.location.pathname === Routes.SCHOOL_WISHLIST.path;

    if (isSchoolMaster || isSchoolWishlistPage) {
        return <SchoolWishlistRemoveButton item={item} type={type} onSubmit={() => onRemove?.(item)} />;
    }

    const handleRemoveFinish = (): void => {
        changeFavoriteItem({ item, add: false, itemIndex: index });
        onRemove?.(item);
        enableButtons();
    };

    const handleRemoveWithAnimation = (): void =>
        foldAnimation(cardRef.current as HTMLDivElement, handleRemoveFinish, REMOVE_ANIMATION_DURATION, true);

    const onClick = (): void => {
        if (areButtonsDisabled) {
            return;
        }

        disableButtons();

        !cardRef.current ? handleRemoveFinish() : handleRemoveWithAnimation();
    };

    return (
        <div className='material-remove-button-wrapper'>
            {type === 'icon' && (
                <Icon
                    active
                    name='favorite'
                    width={24}
                    height={24}
                    disabled={areButtonsDisabled}
                    onClick={onClick}
                    className='material-remove-button'
                    animated
                />
            )}

            {type === 'removeOnly' && (
                <Button variant={BUTTON_VARIANTS.SECONDARY} onClick={onClick} disabled={areButtonsDisabled}>
                    {t('cart.cartItems.remove')}
                </Button>
            )}
        </div>
    );
};

const translate = translation('cart');

export default translate(MaterialRemoveButton);
